<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';

interface Lesson {
  id: string;
  title: string;
  content: string | null;
  order: number;
  state: string;
  skipped: boolean;
  lessonGoals: string[];
  retentionReviewedAt: string | null;
}

interface Curriculum {
  id: string;
  title: string;
  description: string | null;
  state: string;
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const curriculum = ref<Curriculum | null>(null);
const lessonList = ref<Lesson[]>([]);
const loading = ref(false);
const error = ref('');
const expandedLessons = ref<Set<string>>(new Set());
// Count of active (non-archived) cards per lesson — drives whether the retention
// button reads "Generate" (none yet) or "Revise" (review topics already exist).
const cardCountByLesson = ref<Record<string, number>>({});
// Cards due in this curriculum right now — powers the per-curriculum Review button.
// (Global review lives in the sidebar; this is the drill-down for one curriculum.)
const reviewsDue = ref(0);

function reviewTopicCount(lessonId: string): number {
  return cardCountByLesson.value[lessonId] ?? 0;
}

// The flag (retentionReviewedAt) decides the verb — so a lesson the learner
// deliberately saved nothing from still reads "Revise", and archiving every card
// later doesn't flip it back to "Generate". The count only adds the "(N)".
function retentionButtonLabel(lesson: Lesson): string {
  if (!lesson.retentionReviewedAt) return 'Generate review topics →';
  const n = reviewTopicCount(lesson.id);
  return n > 0 ? `Revise review topics (${n}) →` : 'Revise review topics →';
}

const nextLessonId = computed(() => {
  const active = lessonList.value.find(
    (l) => l.state === 'teaching' && !l.skipped
  );
  if (active) return active.id;
  const next = lessonList.value.find(
    (l) => l.state !== 'passed' && !l.skipped
  );
  return next?.id ?? null;
});

function toggleLesson(lessonId: string) {
  if (expandedLessons.value.has(lessonId)) {
    expandedLessons.value.delete(lessonId);
  } else {
    expandedLessons.value.add(lessonId);
  }
}

function lessonStateLabel(state: string): string {
  const labels: Record<string, string> = {
    pending: 'Pending',
    teaching: 'In Progress',
    reviewing: 'Reviewing',
    passed: 'Passed',
  };
  return labels[state] || state;
}

async function fetchData() {
  if (!user.value) return;
  const id = route.params.id as string;
  loading.value = true;
  error.value = '';

  try {
    curriculum.value = await trpc.getCurriculum.query({ id });

    const [lessonsList, cards] = await Promise.all([
      trpc.listLessons.query({ curriculumId: id }),
      trpc.listFlashcards.query({ curriculumId: id, limit: 500 }) as Promise<
        Array<{ lessonId: string | null; due: string }>
      >,
    ]);
    const sorted = lessonsList.sort(
      (a: { order: number }, b: { order: number }) => a.order - b.order
    );
    lessonList.value = sorted;

    const now = Date.now();
    const counts: Record<string, number> = {};
    let due = 0;
    for (const card of cards) {
      if (card.lessonId) counts[card.lessonId] = (counts[card.lessonId] ?? 0) + 1;
      if (new Date(card.due).getTime() <= now) due += 1;
    }
    cardCountByLesson.value = counts;
    reviewsDue.value = due;

    // Expand all lessons by default
    expandedLessons.value = new Set(sorted.map((l) => l.id));
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load curriculum';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }
  fetchData();
});

watch(user, (newUser) => {
  if (newUser) fetchData();
});

watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) fetchData();
  }
);
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>
    <div class="flex-1 overflow-y-auto scrollbar-thin">
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div
          v-if="isLoading || loading"
          class="text-center text-muted-foreground"
        >
          Loading...
        </div>

        <div
          v-else-if="error"
          class="space-y-4"
        >
          <div class="bg-destructive/10 text-destructive p-4 rounded-md">
            {{ error }}
          </div>
          <Button
            variant="ghost"
            @click="router.push('/learn')"
          >
            Back to Curricula
          </Button>
        </div>

        <div
          v-else-if="curriculum"
          class="space-y-6"
        >
          <!-- Header -->
          <div>
            <button
              class="text-sm text-muted-foreground hover:text-foreground mb-2"
              @click="router.push('/learn')"
            >
              &larr; Back to Curricula
            </button>
            <h1 class="text-3xl font-bold">
              {{ curriculum.title }}
            </h1>
            <p
              v-if="curriculum.description"
              class="text-muted-foreground mt-2"
            >
              {{ curriculum.description }}
            </p>
            <div class="mt-4 flex items-center gap-2 flex-wrap">
              <Button
                v-if="nextLessonId"
                @click="router.push({ path: `/learn/${curriculum.id}/tutor`, query: { lessonId: nextLessonId } })"
              >
                {{ lessonList.some((l) => l.state === 'teaching' && !l.skipped) ? 'Continue Learning' : 'Start Learning' }}
              </Button>
              <div
                v-else-if="lessonList.length > 0"
                class="text-sm text-green-600 dark:text-green-400 font-medium"
              >
                All lessons completed!
              </div>
              <Button
                v-if="reviewsDue > 0"
                variant="secondary"
                @click="router.push(`/learn/${curriculum.id}/review`)"
              >
                Review ({{ reviewsDue }})
              </Button>
              <Button
                variant="outline"
                @click="router.push(`/learn/${curriculum.id}/items`)"
              >
                View items
              </Button>
            </div>
          </div>

          <!-- Lessons -->
          <div class="space-y-3">
            <h2 class="text-xl font-semibold">
              Lessons ({{ lessonList.length }})
            </h2>

            <div
              v-for="(lesson, index) in lessonList"
              :key="lesson.id"
              class="bg-card rounded-lg border border-border overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/50 transition-colors"
                @click="toggleLesson(lesson.id)"
              >
                <span
                  class="text-muted-foreground text-sm font-mono w-6"
                  :class="lesson.skipped ? 'line-through' : ''"
                >{{ index + 1 }}</span>
                <span
                  class="text-xs transition-transform"
                  :class="expandedLessons.has(lesson.id) ? 'rotate-90' : ''"
                >&#9654;</span>
                <span
                  class="font-medium flex-1"
                  :class="lesson.skipped ? 'line-through opacity-60' : ''"
                >{{ lesson.title }}</span>
                <span class="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {{ lesson.skipped ? 'Skipped' : lessonStateLabel(lesson.state) }}
                </span>
              </button>

              <div
                v-if="expandedLessons.has(lesson.id)"
                class="px-4 pb-4 border-t border-border"
              >
                <p
                  v-if="lesson.content"
                  class="text-sm text-muted-foreground mt-3 mb-3"
                >
                  {{ lesson.content }}
                </p>

                <div
                  v-if="lesson.lessonGoals.length > 0"
                  class="space-y-2 mt-3"
                >
                  <h4 class="text-sm font-medium text-muted-foreground">
                    Lesson Goals
                  </h4>
                  <ul class="space-y-1.5">
                    <li
                      v-for="(goal, goalIdx) in lesson.lessonGoals"
                      :key="goalIdx"
                      class="flex items-start gap-2 text-sm"
                    >
                      <span class="text-muted-foreground mt-0.5 shrink-0">&#8226;</span>
                      <span class="flex-1">{{ goal }}</span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="lesson.state === 'passed' && !lesson.skipped"
                  class="mt-4 flex flex-wrap gap-2"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    @click="router.push({ path: `/learn/${curriculum!.id}/retention`, query: { lessonId: lesson.id } })"
                  >
                    {{ retentionButtonLabel(lesson) }}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    @click="router.push({ path: `/learn/${curriculum!.id}/tutor`, query: { lessonId: lesson.id } })"
                  >
                    Reopen lesson chat
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
